import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '../hooks/usePageTitle';
import { Sun, Moon, Monitor, Check, Link2, Download, ShieldCheck, Eye, Shield, QrCode, Users, Scan } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { authApi } from '../api/auth';
import { statsApi, PlatformStats } from '../api/stats';
import { getMachhaAsset, AssetImg } from '../lib/machhaAsset';
import { clayStaggerContainer, clayStaggerItem, clayTapScale, clayHoverBounce } from '../constants/animations';
import { Eyebrow } from '../components/UI/Eyebrow';
import { SectionTitle } from '../components/UI/SectionTitle';
import { Reveal } from '../components/UI/Reveal';
import { CTAButton } from '../components/UI/CTAButton';
import { CountUpStat } from '../components/UI/CountUpStat';

import styles from './LandingPage.module.css';

const lpThemes = [
  { value: 'light' as const, icon: Sun, label: 'Light' },
  { value: 'dark' as const, icon: Moon, label: 'Dark' },
  { value: 'system' as const, icon: Monitor, label: 'System' },
];

// ── Clay asset helper ──────────────────────────────────────────────────────────

const LPAsset: React.FC<{
  name: string;
  alt: string;
  className?: string;
}> = ({ name, alt, className = '' }) => (
  <AssetImg
    src={getMachhaAsset('LandingPageAssets', name)}
    alt={alt}
    draggable={false}
    className={`${styles['lp-asset']} ${className}`}
  />
);

const Divider = () => <div className={styles['divider']} />;

// ── Hero flow animation ────────────────────────────────────────────────────────

// Real event photos served from CloudFront CDN
const EVENT_PHOTO_BASE = `${import.meta.env.VITE_MACHHA_ASSETS_BASE_URL ?? 'https://d3ic3ju44zc444.cloudfront.net/assets'}/landing/event-photos`;

const LIVE_MOCK_PHOTOS = [
  `${EVENT_PHOTO_BASE}/IMG_3462.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3466.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3469.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3470.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3471.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3467.jpg`,
];

const SIM_PHOTOS = [
  `${EVENT_PHOTO_BASE}/IMG_3462.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3469.jpg`,
  `${EVENT_PHOTO_BASE}/IMG_3471.jpg`,
];

// ── Live Simulation Card ───────────────────────────────────────────────────

const PLATFORM_ICONS = [
  { asset: 'platform-instagram-clay', label: 'Instagram' },
  { asset: 'platform-linkedin-clay',  label: 'LinkedIn'  },
  { asset: 'platform-x-clay',         label: 'X'         },
  { asset: 'platform-facebook-clay',  label: 'Facebook'  },
];

const LIVE_STAGES = [
  { id: 'capture' as const, emoji: '📸', label: 'Photo captured', detail: 'Auto-uploads via FTP', duration: 2200 },
  { id: 'reaching' as const, emoji: '👥', label: 'Reaches your team', detail: 'Social team selects & posts', duration: 2400 },
  { id: 'published' as const, emoji: '🚀', label: 'Live on feed!', detail: '', duration: 2400 },
  { id: 'guests' as const, emoji: '🎞️', label: 'Guests see it instantly', detail: '', duration: 2600 },
];

const PUBLISHED_IDX = LIVE_STAGES.findIndex(s => s.id === 'published');
// last icon delay + spring settle ≈ 0.1 + 3×0.28 + 0.5s ≈ 1.44s
const PUBLISHED_TICK_DELAY = 1500;

const LiveSimCard: React.FC = () => {
  const [stageIdx, setStageIdx] = useState(0);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [publishedDone, setPublishedDone] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = (stageIdx + 1) % LIVE_STAGES.length;
      if (next === 0) setPhotoIdx(p => (p + 1) % SIM_PHOTOS.length);
      setPublishedDone(false);
      setStageIdx(next);
    }, LIVE_STAGES[stageIdx].duration);
    return () => clearTimeout(id);
  }, [stageIdx]);

  useEffect(() => {
    if (stageIdx !== PUBLISHED_IDX) return;
    const id = setTimeout(() => setPublishedDone(true), PUBLISHED_TICK_DELAY);
    return () => clearTimeout(id);
  }, [stageIdx]);

  return (
    <div className={styles['sim-card-wrap']}>
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
      >
        {/* Red ambient glow */}
        <div className={styles['sim-card-glow']} />

        <div className={styles['sim-card']}>

          {/* Header */}
          <div className={styles['sim-card-header']}>
            <div className={styles['sim-card-header-left']}>
              <span className={`${styles['live-dot']} lp-live-dot`} />
              <span className={styles['sim-card-title']}>Live Feed</span>
            </div>
            <span className={styles['sim-card-badge']}>
              TechSummit 2026
            </span>
          </div>

          {/* Photo preview */}
          <div className={styles['sim-photo-wrap']}>
            <AnimatePresence mode="wait">
              <motion.img
                key={photoIdx}
                src={SIM_PHOTOS[photoIdx]}
                alt="Event photo"
                className={styles['sim-photo-img']}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatePresence>

            {/* Stage overlays */}
            <AnimatePresence>
              {/* ── CAPTURE: photo appears, then shutter flash ── */}
              {stageIdx === 0 && (
                <motion.div
                  key={`flash-${photoIdx}`}
                  className={styles['sim-flash']}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0, 0.9, 0] }}
                  transition={{ duration: 0.7, delay: 1.0, times: [0, 0.01, 0.3, 1], ease: 'easeOut' }}
                />
              )}

              {/* ── REACHING: "New photo" notification badge ── */}
              {stageIdx === 1 && (
                <motion.div
                  className={styles['sim-new-badge']}
                  initial={{ opacity: 0, scale: 0.8, x: 8 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9, x: 8 }}
                  transition={{ duration: 0.25 }}
                >
                  <span className={`${styles['sim-new-dot']} lp-live-dot`} />
                  New photo
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Pipeline stages — fixed h-[72px] per row, height never changes */}
          <div className={styles['sim-stages']}>
            {LIVE_STAGES.map((stage, i) => {
              const isActive = stageIdx === i;
              const isDone = stageIdx > i;
              return (
                <div
                  key={stage.id}
                  className={`${styles['sim-stage-row']} ${isActive ? styles['sim-stage-row-active'] : ''}`}
                >
                  {/* Emoji pill */}
                  <span className={`${styles['sim-stage-emoji']} ${isActive ? styles['sim-stage-emoji-active'] : ''}`}>
                    {stage.emoji}
                  </span>

                  {/* Label + secondary */}
                  <div className={styles['sim-stage-content']}>
                    <p className={`${styles['sim-stage-label']} ${isActive ? styles['sim-stage-label-active'] : ''}`}>
                      {stage.label}
                    </p>
                    {stage.id === 'published' ? (
                      <div className={styles['sim-published-row']}>
                        <motion.span
                          className={styles['sim-posted-text']}
                          initial={false}
                          animate={{ opacity: isActive ? 1 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          Posted →
                        </motion.span>
                        <div className={styles['sim-platform-icons']}>
                          {PLATFORM_ICONS.map(({ asset, label: lbl }, idx) => (
                            <motion.img
                              key={idx}
                              src={getMachhaAsset('LandingPageAssets', asset)}
                              alt={lbl}
                              className={styles['sim-platform-icon']}
                              initial={false}
                              animate={isActive ? { scale: 1, opacity: 1, y: 0 } : { scale: 0.2, opacity: 0, y: 6 }}
                              transition={{ type: 'spring', stiffness: 520, damping: 20, delay: isActive ? 0.1 + idx * 0.28 : 0 }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : stage.id === 'guests' ? (
                      <div className={styles['sim-guest-chips']}>
                        <motion.span
                          className={styles['sim-guest-live-chip']}
                          initial={false}
                          animate={isActive ? { scale: 1, opacity: 1, y: 0 } : { scale: 0.7, opacity: 0, y: 5 }}
                          transition={{ type: 'spring', stiffness: 520, damping: 20, delay: isActive ? 0.08 : 0 }}
                        >
                          <span className={styles['sim-guest-chip-dot']} />
                          47 guests
                        </motion.span>
                        <motion.span
                          className={styles['sim-faceid-chip']}
                          initial={false}
                          animate={isActive ? { scale: 1, opacity: 1, y: 0 } : { scale: 0.7, opacity: 0, y: 5 }}
                          transition={{ type: 'spring', stiffness: 520, damping: 20, delay: isActive ? 0.32 : 0 }}
                        >
                          Face ID
                        </motion.span>
                      </div>
                    ) : (
                      <div className={styles['sim-detail-wrap']}>
                        <motion.p
                          className={styles['sim-detail-text']}
                          initial={false}
                          animate={{ opacity: isActive ? 1 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {stage.detail}
                        </motion.p>
                        {stage.id === 'reaching' && (
                          <div className={styles['sim-progress-track']}>
                            <motion.div
                              className={styles['sim-progress-fill']}
                              initial={{ width: '0%' }}
                              animate={isActive ? { width: '88%' } : { width: '0%' }}
                              transition={isActive ? { duration: 2.1, ease: 'easeInOut' } : { duration: 0 }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className={styles['sim-status']}>
                    {isActive && !(stage.id === 'published' && publishedDone) && (
                      <span className={`${styles['sim-status-dot']} lp-live-dot`} />
                    )}
                    {(isDone || (stage.id === 'published' && publishedDone)) && (
                      <motion.span
                        className={styles['sim-status-check']}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 600, damping: 20 }}
                      >
                        <Check size={11} strokeWidth={3} className={styles['sim-check-icon']} />
                      </motion.span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Live dashboard mock ────────────────────────────────────────────────────────

const ACTIVITY_POOL = [
  { msg: '+12 photos uploaded', color: 'text-candy-green-500', dot: 'bg-candy-green-500' },
  { msg: 'Exported to Instagram', color: 'text-clay-orange', dot: 'bg-clay-orange' },
  { msg: 'Team active: 4 members', color: 'text-clay-sky', dot: 'bg-clay-sky' },
  { msg: '+3 new photos', color: 'text-candy-green-500', dot: 'bg-candy-green-500' },
  { msg: 'LinkedIn export ready', color: 'text-clay-indigo', dot: 'bg-clay-indigo' },
  { msg: 'Overlay applied', color: 'text-clay-amber', dot: 'bg-clay-amber' },
  { msg: '+8 photos in queue', color: 'text-candy-green-500', dot: 'bg-candy-green-500' },
  { msg: 'Facebook ZIP ready', color: 'text-clay-orange', dot: 'bg-clay-orange' },
  { msg: 'Priya joined via event link', color: 'text-candy-green-500', dot: 'bg-candy-green-500' },
  { msg: 'Face scan: 9 photos matched', color: 'text-clay-orange', dot: 'bg-clay-orange' },
  { msg: '47 guests viewing live', color: 'text-candy-green-500', dot: 'bg-candy-green-500' },
];

type ActivityItem = typeof ACTIVITY_POOL[number] & { id: number };

const LiveDashboardMock: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: false, margin: '-100px' });
  const [items, setItems] = useState<ActivityItem[]>([
    { id: 1, ...ACTIVITY_POOL[0] },
    { id: 2, ...ACTIVITY_POOL[1] },
    { id: 3, ...ACTIVITY_POOL[2] },
  ]);
  const poolIdxRef = useRef(3);

  useEffect(() => {
    if (!inView) return;
    const intervalId = setInterval(() => {
      const next = ACTIVITY_POOL[poolIdxRef.current % ACTIVITY_POOL.length];
      poolIdxRef.current++;
      setItems(prev => [{ id: Date.now(), ...next }, ...prev.slice(0, 3)]);
    }, 2200);
    return () => clearInterval(intervalId);
  }, [inView]);

  // Map Tailwind color classes to CSS module classes
  const dotClassMap: Record<string, string> = {
    'bg-candy-green-500': styles['bg-candy-green'],
    'bg-clay-orange': styles['bg-clay-orange'],
    'bg-clay-sky': styles['bg-clay-sky'],
    'bg-clay-indigo': styles['bg-clay-indigo'],
    'bg-clay-amber': styles['bg-clay-amber'],
  };
  const colorClassMap: Record<string, string> = {
    'text-candy-green-500': styles['text-candy-green'],
    'text-clay-orange': styles['text-clay-orange'],
    'text-clay-sky': styles['text-clay-sky'],
    'text-clay-indigo': styles['text-clay-indigo'],
    'text-clay-amber': styles['text-clay-amber'],
  };

  return (
    <div
      ref={ref}
      className={styles['dashboard-card']}
    >
      {/* Header */}
      <div className={styles['dashboard-header']}>
        <div className={styles['dashboard-header-left']}>
          <span className={`${styles['dashboard-live-dot']} lp-live-dot`} />
          <span className={styles['dashboard-title']}>Live Feed</span>
        </div>
        <span className={styles['dashboard-badge']}>
          TechSummit 2026
        </span>
      </div>

      {/* Photo grid — real event photos */}
      <div className={styles['dashboard-grid']}>
        {LIVE_MOCK_PHOTOS.map((src, i) => (
          <motion.div
            key={i}
            className={styles['dashboard-photo-cell']}
            initial={{ opacity: 0, scale: 0.88 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.88 }}
            transition={{ duration: 0.38, delay: i * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <img
              src={src}
              alt=""
              aria-hidden="true"
              className={styles['dashboard-photo']}
              loading="lazy"
            />
          </motion.div>
        ))}
      </div>

      {/* Guest access bar */}
      <div className={styles['dashboard-guest-bar']}>
        <div className={styles['dashboard-guest-bar-left']}>
          <QrCode className={styles['dashboard-guest-bar-icon']} />
          <span className={styles['dashboard-guest-bar-url']}>px.ly/techsummit26</span>
        </div>
        <div className={styles['dashboard-guest-bar-right']}>
          <span className={styles['dashboard-guest-count']}>
            <span className={styles['dashboard-guest-count-dot']} />
            47 guests
          </span>
          <span className={styles['dashboard-faceid-badge']}>
            <Scan className={styles['dashboard-faceid-badge-icon']} />
            Face ID
          </span>
        </div>
      </div>

      {/* Activity log */}
      <div className={styles['dashboard-activity']}>
        <p className={styles['dashboard-activity-title']}>
          Activity
        </p>
        <div className={styles['dashboard-activity-list']}>
          <AnimatePresence mode="popLayout">
            {items.map(item => (
              <motion.div
                key={item.id}
                className={styles['dashboard-activity-row']}
                initial={{ opacity: 0, x: 14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -14 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
                layout
              >
                <span className={`${styles['dashboard-activity-dot']} ${dotClassMap[item.dot] || ''}`} />
                <span className={`${styles['dashboard-activity-msg']} ${colorClassMap[item.color] || ''}`}>{item.msg}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// ── Interactive platforms ─────────────────────────────────────────────────────

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', asset: 'platform-instagram-clay', formats: ['Square 1:1', 'Portrait 4:5', 'Stories 9:16', 'Landscape 1.91:1'], colorClass: 'text-clay-rose' as const, count: 4 },
  { id: 'linkedin', label: 'LinkedIn', asset: 'platform-linkedin-clay', formats: ['Landscape 1.91:1', 'Square 1:1'], colorClass: 'text-clay-sky' as const, count: 2 },
  { id: 'x', label: 'X / Twitter', asset: 'platform-x-clay', formats: ['Landscape 16:9', 'Square 1:1'], colorClass: 'text-primary' as const, count: 2 },
  { id: 'facebook', label: 'Facebook', asset: 'platform-facebook-clay', formats: ['Landscape 1.91:1', 'Square 1:1', 'Stories 9:16'], colorClass: 'text-clay-indigo' as const, count: 3 },
] as const;

const InteractivePlatforms: React.FC = () => {
  const [active, setActive] = useState<string>('instagram');
  const platform = PLATFORMS.find(p => p.id === active)!;

  return (
    <div className={styles['platforms-wrap']}>
      {/* Pills */}
      <div className={styles['platforms-pills']}>
        {PLATFORMS.map(p => (
          <motion.button
            key={p.id}
            onClick={() => setActive(p.id)}
            className={`${styles['platform-pill']} ${active === p.id ? styles['platform-pill-active'] : ''}`}
            whileTap={clayTapScale}
          >
            <LPAsset name={p.asset} alt={p.label} className={styles['platform-pill-icon']} />
            {p.label}
          </motion.button>
        ))}
      </div>

      {/* Preview card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.22 }}
          className={styles['platform-preview']}
        >
          <div className={styles['platform-preview-header']}>
            <LPAsset name={platform.asset} alt={platform.label} className={styles['platform-preview-icon']} />
            <div>
              <p className={`${styles['platform-preview-name']} ${styles[platform.colorClass]}`}>{platform.label}</p>
              <p className={styles['platform-preview-count']}>{platform.count} export formats</p>
            </div>
          </div>
          <div className={styles['platform-formats']}>
            {platform.formats.map(f => (
              <span
                key={f}
                className={styles['platform-format-tag']}
              >
                {f}
              </span>
            ))}
          </div>
          <p className={styles['platform-footer-text']}>
            All exported in a single ZIP — perfect dimensions, no cropping needed.
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ── Confetti particles ─────────────────────────────────────────────────────────

const CONFETTI_COLORS = ['#ef4444', '#f472b6', '#4fd1c5', '#f6c343', '#38bdf8', '#f87171'];

function makeConfetti() {
  return Array.from({ length: 18 }, (_, i) => ({
    id: i,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    dx: (Math.random() - 0.5) * 240,
    dy: -(Math.random() * 130 + 60),
    rotate: Math.random() * 360,
    size: Math.random() * 6 + 5,
  }));
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const LandingPage: React.FC = () => {
  usePageTitle('Live Event Photography');
  const { user } = useAuthStore();
  const { theme, setTheme, resolvedTheme } = useThemeStore();
  const navigate = useNavigate();
  const [confetti, setConfetti] = useState(false);
  const [stats, setStats] = useState<PlatformStats | null>(null);

  const confettiParticles = useMemo(() => makeConfetti(), []);

  useEffect(() => {
    statsApi.getPlatformStats().catch(() => {
      // Fallback to placeholder values if API fails
      setStats({ activeUsers: 2400, eventsCreated: 8900, photosServed: 1200000 });
    }).then(data => {
      if (data) setStats(data);
    });
  }, []);

  const handleCTA = () => {
    if (user) navigate('/albums');
    else window.location.href = authApi.googleLoginUrl();
  };

  const cycleTheme = () => {
    const idx = lpThemes.findIndex(t => t.value === theme);
    setTheme(lpThemes[(idx + 1) % lpThemes.length].value);
  };
  const ActiveThemeIcon = (lpThemes.find(t => t.value === theme) ?? lpThemes[2]).icon;

  const triggerConfetti = () => {
    setConfetti(true);
    setTimeout(() => setConfetti(false), 1200);
  };

  // Navbar: hidden at top, revealed after scrolling past first fold
  const [navVisible, setNavVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setNavVisible(window.scrollY > window.innerHeight * 0.85);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Timeline scroll-progress
  const timelineRef = useRef<HTMLElement>(null);
  const { scrollYProgress: timelineScroll } = useScroll({
    target: timelineRef,
    offset: ['start 60%', 'end 40%'],
  });
  const progressWidth = useTransform(timelineScroll, [0, 1], ['0%', '100%']);


  const hiwSteps = [
    {
      step: 1,
      asset: 'hiw-step1-camera-cloud',
      title: "Connect your camera. That's it.",
      body: "Create an event, type the generated FTP credentials into your camera's WiFi settings once. Canon, Sony, Nikon, Fujifilm — every shot uploads automatically from that point on.",
      highlight: "📷 Set it once — uploads happen automatically. Your team gets every frame the instant it's shot.",
    },
    {
      step: 2,
      asset: 'hiw-step2-social-manager',
      title: 'Photos appear instantly. No waiting.',
      body: "Your social team's gallery refreshes in real time. A banner pops when new photos arrive — click to load them instantly. The photographer never stops shooting. The team never waits.",
      highlight: '🔴 CEO keynote moment live on LinkedIn before the standing ovation has even ended.',
    },
    {
      step: 3,
      asset: 'hiw-step3-export-zip',
      title: 'Edit. Export. Post. Done.',
      body: 'Tweak brightness, drop your watermark overlay, select platforms, and export a ZIP with perfect dimensions for Instagram, LinkedIn, Facebook, and X — all at once.',
      highlight: '✨ One export config covers every platform and dimension. Set it once, reuse it for every event.',
    },
  ];

  return (
    <div className={styles['page']}>

      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <motion.nav
        className={styles['navbar']}
        initial={{ y: -60, opacity: 0 }}
        animate={navVisible ? { y: 0, opacity: 1 } : { y: -60, opacity: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <Link to="/" className={styles['nav-logo-link']}>
          <AssetImg
            src={getMachhaAsset('LogoImages', 'pixelmachha_icon_logo', 1)}
            alt="Pixel Machha"
            className={styles['nav-logo-img']}
          />
          <span className={styles['nav-logo-text']}>Pixel Machha</span>
        </Link>
        <div className={styles['nav-actions']}>
          {/* Mobile: single cycling icon */}
          <button
            className={styles['nav-theme-mobile']}
            aria-label="Cycle theme"
            onClick={cycleTheme}
          >
            <ActiveThemeIcon className={styles['nav-theme-icon']} />
          </button>
          {/* SM+: full 3-icon tab switcher */}
          <div
            className={styles['nav-theme-desktop']}
            role="radiogroup"
            aria-label="Theme preference"
          >
            {lpThemes.map(({ value, icon: Icon, label }) => {
              const isActive = theme === value;
              return (
                <button
                  key={value}
                  role="radio"
                  aria-checked={isActive}
                  aria-label={label}
                  className={`${styles['nav-theme-btn']} ${isActive ? styles['nav-theme-btn-active'] : ''}`}
                  onClick={() => setTheme(value)}
                >
                  {isActive && (
                    <motion.span
                      layoutId="lp-theme-pill"
                      className={styles['nav-theme-pill']}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <Icon className={styles['nav-theme-btn-icon']} />
                </button>
              );
            })}
          </div>
          <CTAButton onClick={handleCTA}>
            <span className={styles['nav-cta-text']}>{user ? 'Dashboard →' : 'Login'}</span>
          </CTAButton>
        </div>
      </motion.nav>

      {/* Ambient red glow — top-centre */}
      <motion.div
        className={styles['glow-primary']}
        animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ repeat: Infinity, duration: 8, ease: 'easeInOut' }}
      />
      {/* Secondary red glow — right side, offset for depth */}
      <motion.div
        className={styles['glow-secondary']}
        animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ repeat: Infinity, duration: 10, ease: 'easeInOut', delay: 2.5 }}
      />

      {/* ── Hero — Split layout ──────────────────────────────────────────────── */}
      <section className={styles['hero']}>

        {/* Floating particles */}
        {[
          { left: '6%', bottom: '22%', dur: '5.5s', delay: '0s', size: 5 },
          { left: '16%', bottom: '12%', dur: '7s', delay: '1.2s', size: 4 },
          { left: '28%', bottom: '28%', dur: '6s', delay: '0.6s', size: 3 },
          { left: '52%', bottom: '8%', dur: '8s', delay: '2s', size: 4 },
          { left: '70%', bottom: '20%', dur: '6.5s', delay: '0.3s', size: 3 },
          { left: '85%', bottom: '14%', dur: '5.8s', delay: '1.6s', size: 4 },
        ].map((p, i) => (
          <div
            key={i}
            aria-hidden="true"
            className={`lp-particle ${styles['hero-particle']}`}
            style={{
              '--p-left': p.left,
              '--p-bottom': p.bottom,
              '--p-size': `${p.size}px`,
              '--dur': p.dur,
              '--delay': p.delay,
            } as React.CSSProperties}
          />
        ))}

        {/* Decorative floaters — top & bottom strips, above/below content text */}

        {/* Top strip — left quarter */}
        <motion.div
          className={`${styles['hero-floater']} ${styles['hero-floater-top-left']}`}
          animate={{ y: [0, -10, 0], rotate: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
        >
          <LPAsset name="decor-clay-pixel-blob" alt="" />
        </motion.div>

        {/* Top strip — right quarter */}
        <motion.div
          className={`${styles['hero-floater']} ${styles['hero-floater-top-right']}`}
          animate={{ y: [0, -8, 0] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut', delay: 1 }}
        >
          <LPAsset name="decor-clay-cloud" alt="" />
        </motion.div>

        {/* Bottom strip — right quarter */}
        <motion.div
          className={`${styles['hero-floater']} ${styles['hero-floater-bottom-right']}`}
          animate={{ y: [0, -12, 0], rotate: [0, -6, 0] }}
          transition={{ repeat: Infinity, duration: 7, ease: 'easeInOut', delay: 0.5 }}
        >
          <LPAsset name="hero-wifi-blob" alt="" />
        </motion.div>

        {/* Bottom strip — left quarter */}
        <motion.div
          className={`${styles['hero-floater']} ${styles['hero-floater-bottom-left']}`}
          animate={{ y: [0, -8, 0], scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut', delay: 1.8 }}
        >
          <LPAsset name="cta-clay-sparkles" alt="" />
        </motion.div>

        {/* Inner content — max-width constrained */}
        <div className={styles['hero-inner']}>
          <div className={styles['hero-grid']}>

            {/* ── LEFT: Messaging (60%) ─────────────────────────────────────── */}
            <motion.div
              className={styles['hero-left']}
              variants={clayStaggerContainer}
              initial="hidden"
              animate="visible"
            >
              {/* Brand name — each character flickers independently */}
              <motion.p variants={clayStaggerItem} className={styles['hero-brand-eyebrow']}>
                {[
                  { ch: 'P', v: 'b', dur: '4.2s', delay: '0.0s' },
                  { ch: 'i', v: 'c', dur: '3.5s', delay: '1.8s' },
                  { ch: 'x', v: 'a', dur: '5.1s', delay: '0.5s' },
                  { ch: 'e', v: 'd', dur: '3.8s', delay: '2.4s' },
                  { ch: 'l', v: 'c', dur: '4.7s', delay: '0.9s' },
                  { ch: ' ', v: null, dur: '',     delay: ''     },
                  { ch: 'M', v: 'a', dur: '3.2s', delay: '1.5s' },
                  { ch: 'a', v: 'd', dur: '4.9s', delay: '0.3s' },
                  { ch: 'c', v: 'b', dur: '3.6s', delay: '2.0s' },
                  { ch: 'h', v: 'c', dur: '4.3s', delay: '1.1s' },
                  { ch: 'h', v: 'a', dur: '3.9s', delay: '2.7s' },
                  { ch: 'a', v: 'd', dur: '5.3s', delay: '0.7s' },
                ].map(({ ch, v, dur, delay }, i) => (
                  <span
                    key={i}
                    className={v ? styles[`flicker-${v}`] : undefined}
                    style={v ? ({ '--fdur': dur, '--fdelay': delay } as React.CSSProperties) : undefined}
                  >
                    {ch}
                  </span>
                ))}
              </motion.p>

              {/* Live badge */}
              <motion.div
                variants={clayStaggerItem}
                className={styles['hero-live-badge']}
              >
                <span className={`${styles['hero-badge-dot']} lp-live-dot`} />
                Built for social media teams at live events
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={clayStaggerItem}
                className={styles['hero-headline']}
              >
                Your event is <span className="lp-gradient-text">LIVE</span><motion.span
                  className={styles['hero-live-dot-char']}
                  animate={{
                    opacity: [1, 0.1, 1],
                    filter: [
                      'drop-shadow(0 0 6px rgba(239,68,68,1)) drop-shadow(0 0 18px rgba(239,68,68,0.7)) drop-shadow(0 0 36px rgba(239,68,68,0.4))',
                      'drop-shadow(0 0 0px rgba(239,68,68,0))',
                      'drop-shadow(0 0 6px rgba(239,68,68,1)) drop-shadow(0 0 18px rgba(239,68,68,0.7)) drop-shadow(0 0 36px rgba(239,68,68,0.4))',
                    ],
                  }}
                  transition={{ duration: 2.2, times: [0, 0.5, 1], repeat: Infinity, ease: 'easeInOut' }}
                >.</motion.span>
                <br />
                Your content should be too.
              </motion.h1>

              {/* Subheading — outcome driven */}
              <motion.p
                variants={clayStaggerItem}
                className={styles['hero-subheading']}
              >
                Capture. Process. Publish.<br />
                <span className={styles['hero-sub-muted']}>All in seconds — not hours.</span>
              </motion.p>

              {/* CTA block */}
              <motion.div
                variants={clayStaggerItem}
                className={styles['hero-cta-block']}
              >
                <div className={styles['hero-cta-primary-wrap']}>
                  <CTAButton onClick={handleCTA} large className={styles['hero-cta-primary-btn']}>
                    {user ? 'Go to Dashboard →' : 'Login'}
                  </CTAButton>
                </div>
                <motion.a
                  href="#how-it-works"
                  className={styles['hero-cta-secondary']}
                  whileHover={clayHoverBounce}
                  whileTap={clayTapScale}
                >
                  See how it works →
                </motion.a>
              </motion.div>

              {/* Micro-trust line */}
              <motion.ul
                variants={clayStaggerItem}
                className={styles['hero-trust-list']}
              >
                {['No setup', 'No delays', 'Works with your existing cameras'].map((item) => (
                  <li
                    key={item}
                    className={`${styles['hero-trust-item']} ${resolvedTheme === 'dark' ? styles['hero-trust-item-dark-shadow'] : ''}`}
                  >
                    <Check size={12} className={styles['hero-check-icon']} strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            {/* ── RIGHT: Live Feed Simulation (40%) ─────────────────────────── */}
            <motion.div
              className={styles['hero-right']}
              initial={{ opacity: 0, x: 32, y: 8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.65, delay: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <LiveSimCard />
            </motion.div>

          </div>
        </div>
      </section>

      <Divider />

      {/* ── The Problem ─────────────────────────────────────────────────────── */}
      <section className={styles['section-centered']} id="problem">
        <Reveal>
          <Eyebrow>The struggle is real</Eyebrow>
          <SectionTitle className={styles['section-mb-4']}>
            Your social team is at the event.<br />
            The photos are stuck in a camera bag.
          </SectionTitle>
          <p className={styles['section-desc']}>
            You're covering a product launch, a keynote, a brand activation. The moment is
            happening live. And your Instagram is still on yesterday's post.
          </p>
        </Reveal>

        <motion.div
          className={styles['card-grid-4']}
          variants={clayStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {[
            {
              asset: 'problem-turtle-memory-card',
              title: 'The Memory Card Relay Race',
              body: `Photographer shoots → waits for a break → pulls out the card reader → copies 400 RAWs → emails a Dropbox link → your social team downloads them → posts one photo — 45 minutes after the moment passed.`,
              fix: 'Photos upload via WiFi while the photographer keeps shooting. Zero relay race.',
            },
            {
              asset: 'problem-confused-format-blob',
              title: 'The Wrong-Format Scramble',
              body: `Your social manager gets a gorgeous landscape photo. Instagram wants a square. Stories want 9:16. LinkedIn wants 1.91:1. They open some janky online cropper and post a blurry mess.`,
              fix: "One click exports the same photo in every platform's exact required dimensions.",
            },
            {
              asset: 'problem-unbranded-ghost',
              title: 'The Unbranded Ghost Post',
              body: `Photo goes out without the event hashtag overlay or company logo. Your brand team has a meltdown. Legal asks questions. The intern apologises for the fourth time this week.`,
              fix: 'Save branded export configs once. Every photo comes out pre-watermarked, every time.',
            },
            {
              asset: 'problem-photo-hunt',
              title: 'The Guest Photo Hunt',
              body: `You shared a 2,000-photo album with attendees. Now every single one of them is scrolling endlessly, squinting at thumbnails, looking for one photo of themselves. Most give up.`,
              fix: 'One selfie. Face recognition finds every photo of them instantly.',
            },
          ].map(card => (
            <motion.div
              key={card.title}
              variants={clayStaggerItem}
              className={styles['clay-card']}
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={styles['clay-card-center']}>
                <LPAsset name={card.asset} alt={card.title} className={styles['clay-card-asset']} />
              </div>
              <h3 className={styles['clay-card-title']}>{card.title}</h3>
              <p className={styles['clay-card-body']}>{card.body}</p>
              <div className={styles['clay-card-fix']}>
                {card.fix}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <Divider />

      {/* ── How It Works — timeline with scroll progress ─────────────────────── */}
      <section ref={timelineRef} className={styles['section-left']} id="how-it-works">
        <Reveal className={`${styles['section-mb-10']} ${styles['text-center']}`}>
          <Eyebrow>How it works</Eyebrow>
          <SectionTitle>Three steps. Zero lag. All live.</SectionTitle>
        </Reveal>

        {/* Progress bar */}
        <div className={styles['hiw-progress-track']}>
          <motion.div
            className={styles['hiw-progress-fill']}
            style={{ width: progressWidth }}
          />
        </div>

        {/* Desktop: 3-column */}
        <div className={styles['hiw-desktop']}>
          {/* Main content row */}
          <div className={styles['hiw-grid-3']}>
            {hiwSteps.map((step, i) => (
              <Reveal key={step.step} delay={i * 0.12}>
                <div className={styles['hiw-step-number']}>
                  {step.step}
                </div>
                <motion.div
                  className={styles['hiw-step-asset-wrap']}
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <LPAsset name={step.asset} alt={step.title} className={styles['hiw-step-asset']} />
                </motion.div>
                <h3 className={styles['hiw-step-title']}>{step.title}</h3>
                <p className={styles['hiw-step-body']}>{step.body}</p>
              </Reveal>
            ))}
          </div>
          {/* Highlight cards row — separate grid so all three share the same row height */}
          <div className={styles['hiw-grid-3']}>
            {hiwSteps.map((step, i) => (
              <Reveal key={`hl-${step.step}`} delay={i * 0.12 + 0.15} className={styles['reveal-h-full']}>
                <div className={styles['hiw-highlight-card']}>
                  {step.highlight}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Mobile: vertical with connecting line */}
        <div className={styles['hiw-mobile']}>
          {[
            {
              step: 1,
              asset: 'hiw-step1-camera-cloud',
              title: 'Connect your camera. That\'s it.',
              body: 'Create an event, type the FTP credentials into your camera once. Every shot uploads automatically.',
              highlight: '📷 Set it once. The camera does the rest.',
            },
            {
              step: 2,
              asset: 'hiw-step2-social-manager',
              title: 'Photos appear instantly. No waiting.',
              body: 'Gallery refreshes in real time. A banner pops up the moment new photos arrive — click to load instantly.',
              highlight: '🔴 Live before the standing ovation ends.',
            },
            {
              step: 3,
              asset: 'hiw-step3-export-zip',
              title: 'Edit. Export. Post. Done.',
              body: 'Tweak, watermark, select platforms, and export a ZIP with every format. All at once.',
              highlight: '✨ One config. Every platform. Every time.',
            },
          ].map((step, i) => (
            <Reveal key={step.step} delay={0.08} className={styles['hiw-mobile-step']}>
              <div className={styles['hiw-mobile-left']}>
                <div className={styles['hiw-step-number']}>
                  {step.step}
                </div>
                {i < 2 && <div className={styles['hiw-mobile-connector']} />}
              </div>
              <div className={styles['hiw-mobile-right']}>
                <LPAsset name={step.asset} alt={step.title} className={styles['hiw-mobile-asset']} />
                <h3 className={styles['hiw-mobile-title']}>{step.title}</h3>
                <p className={styles['hiw-mobile-body']}>{step.body}</p>
                <p className={styles['hiw-mobile-highlight']}>{step.highlight}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Features — 3 cards, new copy ─────────────────────────────────────── */}
      <section className={styles['section-left']} id="features">
        <Reveal className={`${styles['text-center']} ${styles['section-mb-14']}`}>
          <Eyebrow>Features</Eyebrow>
          <SectionTitle>
            Everything your event social team needs.<br />
            Nothing they don't.
          </SectionTitle>
        </Reveal>

        <motion.div
          className={styles['card-grid-3']}
          variants={clayStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {[
            {
              asset: 'feature-ftp-antenna',
              title: 'Capture → Instantly Connected',
              desc: 'Plug in your camera. We handle the rest — no cables, no chaos. Every shot transfers wirelessly the moment the shutter fires.',
            },
            {
              asset: 'feature-live-record-button',
              title: 'Photos land before the moment ends',
              desc: 'Your team sees photos while the event is still happening. A live gallery that updates in real time — not after the applause stops.',
            },
            {
              asset: 'feature-export-configs-box',
              title: 'Edit once. Publish everywhere.',
              desc: 'Resize, brand, and export for every platform in seconds. One config. Every format. Delivered as a ZIP — watermarked, on-brand, ready to post.',
            },
          ].map(feature => (
            <motion.div
              key={feature.title}
              variants={clayStaggerItem}
              className={styles['clay-card']}
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <motion.div
                className={styles['clay-card-center']}
                whileHover={{ scale: 1.08, rotate: 4 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <LPAsset name={feature.asset} alt={feature.title} className={styles['feature-card-asset']} />
              </motion.div>
              <h3 className={styles['feature-card-title']}>{feature.title}</h3>
              <p className={styles['clay-card-body-secondary']}>{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <Divider />

      {/* ── Live Dashboard ───────────────────────────────────────────────────── */}
      <section className={styles['section-left']}>
        <div className={styles['dashboard-section-grid']}>
          <Reveal>
            <Eyebrow>See it live</Eyebrow>
            <SectionTitle className={styles['section-mb-5']}>
              Your team — and your guests — see photos the moment they're shot.
            </SectionTitle>
            <p className={styles['dashboard-section-desc']}>
              No Slack messages. No "did you send the link?" No waiting for the photographer to
              get back to the office. The gallery updates itself — and with one shareable link,
              guests browse photos live. Face recognition finds their own in seconds.
            </p>
            <ul className={styles['dashboard-section-list']}>
              {[
                'Gallery refreshes every 15 seconds',
                '"New photos" banner appears instantly',
                'Entire team sees the same photos simultaneously',
                'No downloads, no cloud sync delays',
                'One link — guests view photos as they\'re taken',
                'Face recognition: guests find their own photos in seconds',
              ].map(item => (
                <li key={item} className={styles['dashboard-section-list-item']}>
                  <span className={styles['dashboard-check-icon']}>✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15} className={styles['dashboard-section-right']}>
            <LiveDashboardMock />
          </Reveal>
        </div>
      </section>

      <Divider />

      {/* ── Platforms — interactive ──────────────────────────────────────────── */}
      <section className={styles['section-centered']}>
        <Reveal className={styles['section-mb-12']}>
          <Eyebrow>Platform support</Eyebrow>
          <SectionTitle className={styles['section-mb-3']}>
            Right dimensions. Every platform. Every time.
          </SectionTitle>
          <p className={styles['section-desc-lg']}>
            No more Googling "what size is an Instagram Story again?" Click a platform to see your formats.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <InteractivePlatforms />
        </Reveal>
      </section>

      <Divider />

      {/* ── Album Sharing — Bento Grid ─────────────────────────────────────── */}
      <section className={styles['section-left']}>
        <Reveal className={`${styles['text-center']} ${styles['section-mb-14']}`}>
          <Eyebrow>Album sharing</Eyebrow>
          <SectionTitle>
            Deliver curated albums to clients,<br />
            stakeholders, and attendees.
          </SectionTitle>
        </Reveal>

        {/* ── Bento grid ── */}
        <div className={styles['bento-grid']}>

          {/* ── Hero card — illustration + tagline ── */}
          <Reveal className={styles['bento-hero-col']}>
            <motion.div
              className={styles['bento-card']}
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              {/* Subtle gradient backdrop */}
              <div className={styles['bento-hero-gradient']} />

              <div className={styles['bento-hero-inner']}>
                <motion.div
                  className={styles['bento-hero-asset-wrap']}
                  whileHover={{ scale: 1.04, rotate: -1.5 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                >
                  <LPAsset name="feature-album-share" alt="Album sharing illustration" className={styles['bento-hero-asset']} />
                </motion.div>

                <p className={styles['bento-hero-title']}>
                  Create an album, drop in a link — your audience gets instant access.
                </p>
                <p className={styles['bento-hero-subtitle']}>
                  High-res, branded event photos delivered without friction.
                </p>
              </div>
            </motion.div>
          </Reveal>

          {/* ── One link to share ── */}
          <Reveal delay={0.08} className={styles['bento-col-5']}>
            <motion.div
              className={styles['bento-card-padded']}
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={`${styles['bento-accent-bar']} ${styles['bento-accent-indigo']}`} />

              <div className={styles['bento-feature-row']}>
                <motion.div
                  className={`${styles['bento-feature-icon']} ${styles['bento-icon-indigo']}`}
                  whileHover={{ scale: 1.12, rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <Link2 className={`${styles['bento-icon-svg']} ${styles['text-clay-indigo']}`} strokeWidth={2.2} />
                </motion.div>
                <div>
                  <h3 className={styles['bento-feature-title']}>One link to share</h3>
                  <p className={styles['bento-feature-desc']}>Generate a unique invite link for every album. Send it via email, Slack, WhatsApp — anywhere.</p>
                </div>
              </div>

              {/* Mock link bar */}
              <div className={styles['bento-link-bar']}>
                <Link2 className={styles['bento-link-icon']} strokeWidth={2} />
                <span className={styles['bento-link-text']}>pixelmachha.com/join/a8kx2m...</span>
                <span className={styles['bento-link-copy']}>Copy</span>
              </div>
            </motion.div>
          </Reveal>

          {/* ── Album preview ── */}
          <Reveal delay={0.16} className={styles['bento-col-5']}>
            <motion.div
              className={styles['bento-card-padded']}
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={`${styles['bento-accent-bar']} ${styles['bento-accent-orange']}`} />

              <div className={styles['bento-feature-row']}>
                <motion.div
                  className={`${styles['bento-feature-icon']} ${styles['bento-icon-orange']}`}
                  whileHover={{ scale: 1.12, rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <Eye className={`${styles['bento-icon-svg']} ${styles['text-clay-orange']}`} strokeWidth={2.2} />
                </motion.div>
                <div>
                  <h3 className={styles['bento-feature-title']}>Album preview</h3>
                  <p className={styles['bento-feature-desc']}>Recipients land on a branded preview page with the album cover and details before they join.</p>
                </div>
              </div>
            </motion.div>
          </Reveal>

          {/* ── View & download ── */}
          <Reveal delay={0.2} className={styles['bento-col-6']}>
            <motion.div
              className={styles['bento-card-padded']}
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={`${styles['bento-accent-bar']} ${styles['bento-accent-green']}`} />

              <div className={styles['bento-feature-row']}>
                <motion.div
                  className={`${styles['bento-feature-icon']} ${styles['bento-icon-green']}`}
                  whileHover={{ scale: 1.12, rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <Download className={`${styles['bento-icon-svg']} ${styles['text-candy-green']}`} strokeWidth={2.2} />
                </motion.div>
                <div>
                  <h3 className={styles['bento-feature-title']}>View & download</h3>
                  <p className={styles['bento-feature-desc']}>Members browse every photo in the album and download high-res copies — individually or in batches.</p>
                </div>
              </div>

              {/* Mock download indicator */}
              <div className={styles['bento-download-row']}>
                <div className={styles['bento-download-track']}>
                  <motion.div
                    className={styles['bento-download-fill']}
                    initial={{ width: '0%' }}
                    whileInView={{ width: '72%' }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 1.2, ease: 'easeOut' }}
                  />
                </div>
                <span className={styles['bento-download-label']}>48 photos</span>
              </div>
            </motion.div>
          </Reveal>

          {/* ── You stay in control ── */}
          <Reveal delay={0.24} className={styles['bento-col-6']}>
            <motion.div
              className={styles['bento-card-padded']}
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={`${styles['bento-accent-bar']} ${styles['bento-accent-rose']}`} />

              <div className={styles['bento-feature-row']}>
                <motion.div
                  className={`${styles['bento-feature-icon']} ${styles['bento-icon-rose']}`}
                  whileHover={{ scale: 1.12, rotate: 8 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                >
                  <ShieldCheck className={`${styles['bento-icon-svg']} ${styles['text-clay-rose']}`} strokeWidth={2.2} />
                </motion.div>
                <div>
                  <h3 className={styles['bento-feature-title']}>You stay in control</h3>
                  <p className={styles['bento-feature-desc']}>Regenerate links to revoke access instantly. See who joined. Remove members anytime.</p>
                </div>
              </div>

              {/* Mock member chips */}
              <div className={styles['bento-member-chips']}>
                {['AK', 'PR', 'SM'].map((initials, idx) => (
                  <span key={initials} className={styles['bento-member-chip']}>
                    <span className={`${styles['bento-member-avatar']} ${idx === 0 ? styles['bento-member-avatar-indigo'] : idx === 1 ? styles['bento-member-avatar-orange'] : styles['bento-member-avatar-green']}`}>{initials}</span>
                    <span className={styles['bento-member-label']}>Joined</span>
                  </span>
                ))}
                <span className={styles['bento-member-more']}>+12 more</span>
              </div>
            </motion.div>
          </Reveal>


        </div>
      </section>

      <Divider />

      {/* ── Face Recognition — dedicated showcase ──────────────────────────── */}
      <section className={styles['section-centered']} id="face-recognition">
        <Reveal>
          <Eyebrow>Face Recognition</Eyebrow>
          <SectionTitle className={styles['section-mb-4']}>
            2,000 photos. One selfie.<br />
            Find yourself instantly.
          </SectionTitle>
          <p className={styles['section-desc']}>
            Your guests shouldn't scroll through an entire gallery to find one photo of themselves.
            With face recognition, they take a single selfie and get a personalized "My Photos" tab in seconds.
          </p>
        </Reveal>

        <motion.div
          className={styles['face-showcase']}
          variants={clayStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {/* Step flow: Selfie → My Photos */}
          {[
            {
              asset: 'feature-face-selfie-search',
              step: '1',
              title: "One selfie. That's it.",
              desc: 'Guest opens the album, takes a quick selfie. No app download, no sign-up — just the camera they already have. Face recognition does the rest in under a second.',
            },
            {
              asset: 'feature-my-photos-tab',
              step: '2',
              title: '"My Photos" — instant.',
              desc: 'A personal tab appears with every photo they\'re in. Download, share, relive the moment — no more scrolling through thousands of shots.',
            },
          ].map((item) => (
            <motion.div
              key={item.step}
              variants={clayStaggerItem}
              className={styles['face-step-card']}
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={styles['face-step-number']}>{item.step}</div>
              <motion.div
                className={styles['face-step-asset-wrap']}
                whileHover={{ scale: 1.08, rotate: 4 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <LPAsset name={item.asset} alt={item.title} className={styles['face-step-asset']} />
              </motion.div>
              <h3 className={styles['face-step-title']}>{item.title}</h3>
              <p className={styles['clay-card-body-secondary']}>{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Admin controls callout */}
        <Reveal delay={0.2}>
          <div className={styles['face-admin-callout']}>
            <Shield size={18} style={{ color: 'var(--clay-orange)', flexShrink: 0 }} />
            <div>
              <p className={styles['face-admin-title']}>You control everything.</p>
              <p className={styles['face-admin-desc']}>
                Enable face recognition per album. Choose if guests see all photos or only their matches.
                Face data is encrypted, never shared, and guests can delete their selfie at any time.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <Divider />

      {/* ── Platform Usage Stats ─────────────────────────────────────────────── */}
      {stats && (
        <section className={styles['metrics-section']}>
          <Reveal>
            <div className={styles['metrics-card']}>
              <Reveal className={`${styles['text-center']} ${styles['section-mb-10']}`}>
                <Eyebrow>Join the community</Eyebrow>
                <SectionTitle>Trusted by teams worldwide.</SectionTitle>
              </Reveal>
              <div className={styles['metrics-grid']}>
                <CountUpStat
                  value={stats.activeUsers.toString()}
                  numericEnd={stats.activeUsers}
                  label="Active Users"
                  emoji="👥"
                  color="text-candy-green-500"
                />
                <CountUpStat
                  value={stats.eventsCreated.toString()}
                  numericEnd={stats.eventsCreated}
                  label="Events Created"
                  emoji="📅"
                  color="text-clay-orange"
                />
                <CountUpStat
                  value={stats.photosServed.toString()}
                  numericEnd={stats.photosServed}
                  label="Photos Served"
                  emoji="📸"
                  color="text-clay-rose"
                />
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <Divider />

      {/* ── Metrics — count-up ──────────────────────────────────────────────── */}
      <section className={styles['metrics-section']}>
        <Reveal>
          <div className={styles['metrics-card']}>
            <Reveal className={`${styles['text-center']} ${styles['section-mb-10']}`}>
              <Eyebrow>By the numbers</Eyebrow>
              <SectionTitle>Numbers that actually matter.</SectionTitle>
            </Reveal>
            <div className={styles['metrics-grid']}>
              <CountUpStat
                value="15s"
                numericEnd={15}
                suffix="s"
                label="From camera to gallery"
                asset="stats-clay-stopwatch"
                color="text-candy-green-500"
              />
              <CountUpStat
                value="11×"
                numericEnd={11}
                suffix="×"
                label="Faster than manual workflow"
                asset="stats-clay-lightning"
                color="text-clay-orange"
              />
              <CountUpStat
                value="4"
                numericEnd={4}
                label="Social platforms, one ZIP"
                asset="stats-platform-stack"
                color="text-clay-rose"
              />
              <CountUpStat
                value="0"
                numericEnd={0}
                label="Excuses for posting late"
                asset="stats-no-usb"
                color="dark:text-text-primary text-text-light-primary"
              />
            </div>
          </div>
        </Reveal>
      </section>

      <Divider />

      {/* ── Who It's For ─────────────────────────────────────────────────────── */}
      <section className={styles['section-centered']} id="who">
        <Reveal>
          <Eyebrow>Who it's for</Eyebrow>
          <SectionTitle className={styles['section-mb-4']}>
            For every team that needs to<br />post before the moment is gone.
          </SectionTitle>
          <p className={styles['section-desc']}>
            If "can you send me the photos?" is in your event-day vocabulary, Pixel Machha is for you.
          </p>
        </Reveal>

        <motion.div
          className={styles['card-grid-4']}
          variants={clayStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {[
            {
              asset: 'persona-corporate-comms',
              title: 'Corporate Comms & PR Teams',
              desc: 'Product launches, investor days, leadership summits, press conferences. Your comms team needs polished, branded photos published the moment the CEO steps off stage — not tomorrow morning.',
              quote: '"Our LinkedIn post went live before the keynote ended. The photographer was still on stage."',
            },
            {
              asset: 'persona-event-social-manager',
              title: 'Event Social Media Managers',
              desc: "Concerts, festivals, sports, conferences. You're running 4 platforms at once, audience is watching you live, and you can't afford a 30-minute photo lag. Live content requires live delivery.",
              quote: '"Covered a 3-day festival. Posted 150+ branded photos in real time. Never touched a USB cable."',
            },
            {
              asset: 'persona-brand-marketer',
              title: 'Brand Marketing Teams',
              desc: "Brand activations, pop-ups, sponsorship events. You've invested in a photographer — now actually use those photos at the event. Watermarked, platform-ready, posted while the crowd is still there.",
              quote: '"Sponsor logo on every export. Legal is happy. Brand team is happy. I am happy."',
            },
            {
              asset: 'persona-event-attendee',
              title: 'Event Attendees & Guests',
              desc: "You went to an event, you know your photo was taken, but the album has 2,000 shots. You're not going to scroll through all of them. One selfie, and face recognition shows you every photo you're in.",
              quote: '"Took a selfie, found 14 photos of me in seconds. Downloaded them all before I left the venue."',
            },
          ].map(card => (
            <motion.div
              key={card.title}
              variants={clayStaggerItem}
              className={styles['clay-card']}
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={styles['clay-card-center']}>
                <LPAsset name={card.asset} alt={card.title} className={styles['clay-card-asset-lg']} />
              </div>
              <h3 className={styles['clay-card-title']}>{card.title}</h3>
              <p className={styles['clay-card-body-secondary']}>{card.desc}</p>
              <p className={styles['persona-quote']}>
                {card.quote}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <Divider />

      {/* ── Final CTA ────────────────────────────────────────────────────────── */}
      <section className={styles['cta-section']} id="cta">
        <Reveal>
          <div className={styles['cta-card']}>
            {/* Background glows */}
            <div className={styles['cta-glow-left']} />
            <div className={styles['cta-glow-right']} />

            {/* Floating sparkles */}
            <motion.div
              className={`${styles['cta-sparkle']} ${styles['cta-sparkle-top']}`}
              animate={{ y: [0, -8, 0], rotate: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
            >
              <LPAsset name="cta-clay-sparkles" alt="" />
            </motion.div>
            <motion.div
              className={`${styles['cta-sparkle']} ${styles['cta-sparkle-bottom']}`}
              animate={{ y: [0, -10, 0], rotate: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut', delay: 1 }}
            >
              <LPAsset name="cta-clay-sparkles" alt="" />
            </motion.div>

            <div className={styles['cta-inner']}>
              {/* Mascot */}
              <motion.div
                className={styles['cta-mascot-wrap']}
                initial={{ scale: 0.7, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ type: 'spring', stiffness: 300, damping: 18 }}
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                >
                  <LPAsset name="cta-celebration-mascot" alt="Celebrating mascot" className={styles['cta-mascot-asset']} />
                </motion.div>
              </motion.div>

              <Eyebrow>Ready to post in real time?</Eyebrow>
              <h2 className={styles['cta-headline']}>
                Your event deserves to be seen{' '}
                <span className="lp-gradient-text">NOW.</span>
                <br />Not tomorrow.
              </h2>
              <p className={styles['cta-desc']}>
                Sign in with Google, create your first event, and your photographer is live uploading in under 3 minutes.
              </p>

              {/* Confetti CTA wrapper */}
              <div className={styles['cta-confetti-wrap']} onMouseEnter={triggerConfetti}>
                <AnimatePresence>
                  {confetti && confettiParticles.map(p => (
                    <motion.div
                      key={p.id}
                      className={styles['cta-confetti-particle']}
                      style={{
                        '--confetti-bg': p.color,
                        '--confetti-size': `${p.size}px`,
                        '--confetti-mt': `${-p.size / 2}px`,
                        '--confetti-ml': `${-p.size / 2}px`,
                      } as React.CSSProperties}
                      initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
                      animate={{ x: p.dx, y: p.dy, opacity: 0, rotate: p.rotate, scale: 0.4 }}
                      transition={{ duration: 0.9, ease: 'easeOut' }}
                    />
                  ))}
                </AnimatePresence>

                {user ? (
                  <CTAButton onClick={handleCTA} large>
                    Go to Dashboard →
                  </CTAButton>
                ) : (
                  <CTAButton href={authApi.googleLoginUrl()} large>
                    <LPAsset name="cta-google-logo-clay" alt="Google" className={styles['cta-google-icon']} />
                    Login
                  </CTAButton>
                )}
              </div>

              <p className={styles['cta-disclaimer']}>
                No credit card. No trial countdown. Just sign in and go live.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className={styles['footer']}>
        <div className={styles['footer-brand']}>
          <img src={getMachhaAsset('LogoImages', 'pixelmachha_icon_logo', 1)} alt="Pixel Machha" className={styles['footer-logo']} />
          Pixel Machha
        </div>
        <span>Live event photography management for social media teams</span>
        <span className={styles['footer-legal-row']}>
          <span>© 2026 Pixel Machha</span>
          <span className={styles['footer-sep']}>·</span>
          <Link to="/legal/privacy" className={styles['footer-legal']}>Privacy Policy</Link>
          <span className={styles['footer-sep']}>·</span>
          <Link to="/legal/terms" className={styles['footer-legal']}>Terms</Link>
        </span>
      </footer>



    </div>
  );
};
